import { injectable } from '~/container'
import { MapSearchContext, SearchFilters } from '../schemas'
import { MapSearchEngineState } from '../types'
import {
  MapSearchMachineInterpreter,
  MapSearchState,
  getMapSearchMachineInterpreted,
} from './machine'
import { Logger } from '../../../composables/useGtmLogRequest'

@injectable()
export class MapSearchEngine {
  abortController = new AbortController()

  constructor(public machine: MapSearchMachineInterpreter) {}

  start() {
    this.machine.start()
  }

  stop() {
    this.machine.stop()
  }

  search(filters: Partial<SearchFilters>) {
    const searchFilters = this.getDefaultFilters(filters)
    const type = this.getSearchType(searchFilters)
    this.abortController.abort()
    this.abortController = new AbortController()
    this.machine.send({
      type,
      filters: searchFilters,
      signal: this.abortController.signal,
    })
  }

  private getDefaultFilters(filters: SearchFilters) {
    return {
      ...filters,
      categoryIds: filters.categoryIds || [],
      facets: filters.facets || [],
    }
  }

  private getSearchType(filters: SearchFilters) {
    if (filters.dates) {
      return 'priceSearch'
    }
    return 'search'
  }

  get status() {
    return this.machine.state.toStrings()[0]
  }

  onContext(listener: (context: MapSearchContext) => void) {
    this.machine.onChange((context) => {
      listener(context)
    })
  }

  onState(listener: (state: MapSearchEngineState) => void) {
    this.machine.onTransition((state) => {
      const stateName = this.getCurrentStateName(state)
      listener(stateName)
    })
  }

  private getCurrentStateName(state: MapSearchState): MapSearchEngineState {
    if (state.matches('searching')) {
      return 'searching'
    }
    if (state.matches('results')) {
      return 'results'
    }
    if (state.matches('error')) {
      return 'error'
    }
    return 'idle'
  }
}

export function getMapSearchEngine(langCode: string, logger: Logger) {
  const machine = getMapSearchMachineInterpreted(langCode, logger)
  return new MapSearchEngine(machine)
}
